import React, { useMemo } from 'react';
import ComboSelector, { TipoClave } from '../../UI/campos/ComboSelector.js';
import CampoEditableFechaCampos from '../../UI/campos/CampoEditableFechaCampos.js';
import CampoEditableTexto from '../../UI/campos/CampoEditableTexto.js';
import MensajeGrabando from '../../UI/MensajeGrabando.js';
import MensajeError from '../../UI/MensajeError.js';
import ResultadoListaPartidos from '../../consulta/ResultadoListaPartidos.js';
import * as Campos from '../../constantes/Campos.js';
import * as Fecha from '../../funciones/FuncionesFecha.js';
import { NombreEquipoTorneo } from '../../funciones/FuncionesEquipo.js';
import fechaParcialImg from '../../../images/fecha-parcial.png';
import libreImg from '../../../images/libre.png';
import styles from './FilaPartidoFixture.module.css';

/**
 * Fila editable para un partido del fixture
 * Muestra campos: ordenPartido (editable), fecha, equipos, botones
 * Los partidos se muestran agrupados por zona, no hay columna zona
 */
const FilaPartidoFixture = (props) => {
  const partido = props.partido;
  const torneo = props.torneo; // Torneo para formatear nombres de equipos
  const esVuelta = props.esVuelta || false; // Si es true, los equipos no son editables
  const mostrarZona = props.mostrarZona || false;
  const mostrarEquipoLibre = props.mostrarEquipoLibre || false;
  const mostrarOrdenPartido = props.mostrarOrdenPartido !== false; // True por defecto
  const mostrarFecha = props.mostrarFecha !== false; // True por defecto
  const mostrarResultados = props.mostrarResultados !== false; // True por defecto
  const esPartidoExistente = props.esPartidoExistente === true; // Indica si es un partido ya grabado
  const zonaObligatoria = props.zonaObligatoria || false;
  const requiereFecha = props.requiereFecha !== false;
  const onChange = props.onChange; // (partido) => void
  const onGuardar = props.onGuardar; // () => void
  const onCancelar = props.onCancelar; // () => void
  const onPartidoElegido = props.onPartidoElegido; // (id) => void - navega al partido
  const isGrabando = props.isGrabando || false; // Estado de grabando individual
  const erroresFila = props.errores || null;

  // Calcular colSpan dinámicamente basado en columnas visibles
  // Columnas: ordenPartido (0/1) + fecha (0/1) + local (1) + resultado (1) + visitante (1) + zona (0/1) + botones (1)
  const colSpan = useMemo(() => {
    let count = 0;
    if (mostrarOrdenPartido) count++;
    if (mostrarFecha) count++;
    count += 4; // local + resultado + visitante + botones
    if (mostrarZona) count++;
    return count;
  }, [mostrarOrdenPartido, mostrarFecha, mostrarZona]);

  // Memoizar equipos para evitar crear array nuevo en cada render
  const equipos = useMemo(() => props.equipos || [], [props.equipos]);

  // Transformar lista de equipos para mostrar nombres con país si es torneo internacional
  const equiposConNombres = useMemo(() => {
    const equiposTransformados = equipos.map((equipo) => ({
      ...equipo,
      nombre: torneo ? NombreEquipoTorneo(equipo, torneo) : equipo.nombre,
    }));
    // Ordenar por nombre alfabéticamente
    return equiposTransformados.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
  }, [equipos, torneo]);

  const onFechaChange = (campo, valor) => {
    onChange({
      anioPartido: valor[Fecha.ANIO],
      mesPartido: valor[Fecha.MES],
      diaPartido: valor[Fecha.DIA],
    });
  };

  const onOrdenPartidoChange = (campo, valor) => {
    onChange({ ordenPartido: valor ? parseInt(valor, 10) : null });
  };

  const onEquipoLocalChange = (equipoSeleccionado) => {
    // Buscar el equipo original en la lista (sin nombre transformado)
    const equipoOriginal =
      equipos.find((e) => e.id === equipoSeleccionado.id) || equipoSeleccionado;
    onChange({ equipoLocal: equipoOriginal });
  };

  const onEquipoVisitanteChange = (equipoSeleccionado) => {
    // Buscar el equipo original en la lista (sin nombre transformado)
    const equipoOriginal =
      equipos.find((e) => e.id === equipoSeleccionado.id) || equipoSeleccionado;
    onChange({ equipoVisitante: equipoOriginal, equipoLibre: false });
  };

  const onEquipoLibreToggle = () => {
    const estaLibre = partido.equipoLibre === true;
    onChange({
      equipoLibre: !estaLibre,
      equipoVisitante: estaLibre ? partido.equipoVisitante : null,
    });
  };

  const onZonaChange = (event) => {
    const valor = event?.target?.value || '';

    // Si el valor está vacío, permitir borrarlo explícitamente
    if (valor === '') {
      onChange({ zona: null });
      return;
    }

    const normalizado = valor
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 1);

    onChange({ zona: normalizado || null });
  };

  const onResultadoLocalChange = (event) => {
    const valor = event?.target?.value;
    const numero =
      valor === '' || valor === null || valor === undefined ? null : Number.parseInt(valor, 10);
    const normalizado = Number.isNaN(numero) ? null : numero;
    const original =
      partido.golesLocalOriginal ?? partido.golesLocal ?? partido.resultadoLocal ?? null;
    onChange({
      golesLocal: normalizado,
      resultadoLocal: normalizado,
      localEditado: normalizado !== original,
    });
  };

  const onResultadoVisitanteChange = (event) => {
    const valor = event?.target?.value;
    const numero =
      valor === '' || valor === null || valor === undefined ? null : Number.parseInt(valor, 10);
    const normalizado = Number.isNaN(numero) ? null : numero;
    const original =
      partido.golesVisitanteOriginal ??
      partido.golesVisitante ??
      partido.resultadoVisitante ??
      null;
    onChange({
      golesVisitante: normalizado,
      resultadoVisitante: normalizado,
      visitanteEditado: normalizado !== original,
    });
  };

  // Validar que el partido tenga datos suficientes para guardar
  const zonaValida = !zonaObligatoria || /^[A-Z0-9]{1}$/.test(partido.zona || '');
  const visitanteValido = partido.equipoLibre === true || !!partido.equipoVisitante;
  const puedeGuardar =
    (!requiereFecha || partido.anioPartido) && partido.equipoLocal && visitanteValido && zonaValida;

  const valorResultadoLocal =
    partido.resultadoLocal !== partido.golesLocal
      ? (partido.golesLocal ?? partido.resultadoLocal ?? '')
      : (partido.golesLocal ?? partido.resultadoLocal ?? '');
  const valorResultadoVisitante =
    partido.resultadoVisitante !== partido.golesVisitante
      ? (partido.golesVisitante ?? partido.resultadoVisitante ?? '')
      : (partido.golesVisitante ?? partido.resultadoVisitante ?? '');

  // Mostrar resultado si existe
  const resultado =
    partido.resultadoLocal != null && partido.resultadoVisitante != null
      ? `${partido.resultadoLocal} - ${partido.resultadoVisitante}`
      : '-';

  // Obtener nombres de equipos con formato correcto
  const nombreLocal =
    torneo && partido.equipoLocal
      ? NombreEquipoTorneo(partido.equipoLocal, torneo)
      : partido.equipoLocal?.nombre || '';

  const nombreVisitante =
    torneo && partido.equipoVisitante
      ? NombreEquipoTorneo(partido.equipoVisitante, torneo)
      : partido.equipoVisitante?.nombre || '';

  return (
    <React.Fragment>
      <tr className={styles.fila}>
        {mostrarOrdenPartido && (
          <td className={styles.ordenPartido}>
            <CampoEditableTexto
              nombre={Campos.Partido.ORDEN_PARTIDO}
              valor={partido.ordenPartido ? partido.ordenPartido.toString() : ''}
              editar={!esPartidoExistente}
              largo={3}
              width="30px"
              onChange={onOrdenPartidoChange}
            />
          </td>
        )}
        {mostrarFecha && (
          <td className={styles.fecha}>
            <div className={styles.fechaWrapper}>
              <CampoEditableFechaCampos
                nombre="fecha"
                anio={partido.anioPartido}
                mes={partido.mesPartido}
                dia={partido.diaPartido}
                editar={true}
                mostrarControlParcial={true}
                usarBotonParcial={true}
                imagenBotonParcial={fechaParcialImg}
                altBotonParcial="Fecha parcial"
                tabStopParcial={false}
                onChange={onFechaChange}
              />
            </div>
          </td>
        )}
        <td className={styles.equipoLocal}>
          {esVuelta ? (
            <span>{nombreLocal}</span>
          ) : (
            <ComboSelector
              tipoClave={TipoClave.ID}
              lista={equiposConNombres}
              campoClave="id"
              campoDescripcion="nombre"
              valorActual={nombreLocal}
              editar={true}
              onItemElegido={onEquipoLocalChange}
              placeholder="Seleccionar equipo local"
            />
          )}
        </td>
        <td className={styles.resultado}>
          {partido.equipoLibre === true ? (
            <span>{resultado}</span>
          ) : esPartidoExistente && !mostrarResultados && onPartidoElegido ? (
            <ResultadoListaPartidos
              id={partido.id}
              local={partido.resultadoLocal}
              visitante={partido.resultadoVisitante}
              onPartidoElegido={onPartidoElegido}
            />
          ) : mostrarResultados ? (
            <div className={styles.resultadoEditable}>
              <input
                type="number"
                min="0"
                max="99"
                inputMode="numeric"
                className={styles.inputResultado}
                value={valorResultadoLocal}
                onChange={onResultadoLocalChange}
                aria-label="Goles local"
              />
              <span className={styles.separadorResultado}>-</span>
              <input
                type="number"
                min="0"
                max="99"
                inputMode="numeric"
                className={styles.inputResultado}
                value={valorResultadoVisitante}
                onChange={onResultadoVisitanteChange}
                aria-label="Goles visitante"
              />
            </div>
          ) : (
            <span>{resultado}</span>
          )}
        </td>
        <td className={styles.equipoVisitante}>
          <div className={styles.visitanteWrapper}>
            {esVuelta ? (
              <span>{nombreVisitante}</span>
            ) : partido.equipoLibre && mostrarEquipoLibre ? (
              <span className={styles.textoLibre}>Libre</span>
            ) : (
              <div className={styles.comboVisitanteContainer}>
                <ComboSelector
                  tipoClave={TipoClave.ID}
                  lista={equiposConNombres}
                  campoClave="id"
                  campoDescripcion="nombre"
                  valorActual={nombreVisitante}
                  editar={true}
                  onItemElegido={onEquipoVisitanteChange}
                  placeholder="Seleccionar equipo visitante"
                />
              </div>
            )}
            {!esVuelta && mostrarEquipoLibre && (
              <button
                type="button"
                className={`${styles.btnToggleIcono} ${partido.equipoLibre ? styles.btnToggleIconoPresionado : ''}`}
                onClick={onEquipoLibreToggle}
                tabIndex={-1}
                title="Equipo libre"
                aria-pressed={partido.equipoLibre === true}
              >
                <img src={libreImg} alt="Libre" className={styles.iconoToggle} />
              </button>
            )}
          </div>
        </td>
        {mostrarZona && (
          <td className={styles.zona}>
            <input
              type="text"
              className={styles.inputZona}
              value={partido.zona || ''}
              onChange={onZonaChange}
              maxLength={1}
              placeholder="-"
              disabled={esPartidoExistente}
            />
          </td>
        )}
        <td className={styles.botones}>
          <button
            className={styles.btnGuardar}
            onClick={onGuardar}
            disabled={!puedeGuardar || isGrabando}
            title="Guardar partido"
          >
            ✓
          </button>
          <button
            className={styles.btnCancelar}
            onClick={onCancelar}
            disabled={isGrabando}
            title="Cancelar"
          >
            ✗
          </button>
        </td>
      </tr>
      {(isGrabando || (erroresFila && erroresFila.length > 0)) && (
        <tr>
          <td colSpan={colSpan} className={styles.filaGrabando}>
            {isGrabando && <MensajeGrabando isGrabando={true} entidad="partido" />}
            {!isGrabando && erroresFila && (
              <MensajeError errores={erroresFila} estilo={styles.filaError} />
            )}
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

export default FilaPartidoFixture;
